<template>
  <div>
    <slot name="label">
      <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
      <label
        v-if="label != null && label.length > 0"
        :for="compontentId"
        class="input-text__label w-100 fs-tiny"
      >
        <strong>{{ label }}</strong>
      </label>
    </slot>
    <div class="input-group">
      <slot name="input-left"></slot>
      <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
      <input
        :id="compontentId"
        :type="type"
        class="form-control input-text__input--transparent ps-0 pe-8 pt-0 pb-2 border-bottom border-rhino rounded-0"
        :class="inputClass"
        :placeholder="placeholder"
        :value="modelValue"
        :autocomplete="autocomplete"
        :readonly="readonly"
        @input="handleInputFn"
      />
      <slot name="input-right"></slot>

      <!-- actions -->
      <div class="input-text__actions position-absolute end-0">
        <button
          v-if="canBeResetted && modelValue.length > 0"
          class="btn"
          type="button"
          @click="() => resetInput"
        >
          <fa-icon class="text-rhino" :icon="faTimes" />
        </button>
        <slot name="action"> </slot>
      </div>
    </div>

    <slot name="hint">
      <p
        v-if="error != null && error.length > 0"
        class="input-text__hint fs-tiny mb-0 mt-2 text-danger"
      >
        {{ error }}
      </p>
      <p
        v-else-if="hint != null && hint.length > 0"
        class="input-text__hint fs-tiny mb-0 mt-2 text-rhino"
      >
        {{ hint }}
      </p>
    </slot>
  </div>
</template>

<script lang="ts">
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useDebounceFn } from '@vueuse/shared';

export default defineComponent({
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    type: {
      type: String as PropType<'text' | 'password' | 'email'>,
      default: 'text',
    },
    autocomplete: {
      required: false,
      type: String as PropType<
        | 'off'
        | 'on'
        | 'name'
        | 'given-name'
        | 'family-name'
        | 'email'
        | 'username'
        | 'new-password'
        | 'current-password'
        | 'one-time-code'
        | 'country'
        | 'postal-code'
        | 'address-line1'
        | 'address-line2'
        | 'address-level1'
        | 'address-level2'
      >,
      default: undefined,
    },
    inputId: {
      type: String,
      required: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    hint: {
      type: String,
      default: null,
    },
    debounce: {
      type: Number,
      default: 0,
    },
    canBeResetted: {
      type: Boolean,
      default: false,
    },
    transparent: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
    inputClass: {
      type: [String, Array] as PropType<string | Array<string>>,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const compontentId = useId();

    const handleInput = (event: InputEvent) => {
      emit('update:modelValue', (event.target as HTMLInputElement).value);
    };

    const handleInputFn =
      props.debounce === 0
        ? handleInput
        : useDebounceFn(handleInput, props.debounce);

    const resetInput = () => {
      emit('update:modelValue', '');
    };

    return {
      faTimes,

      compontentId,
      handleInputFn,
      resetInput,
    };
  },
});
</script>

<style scoped lang="scss">
@import 'gportal-theme/scss/colors';

.input-text {
  &__label {
    &::first-letter {
      text-transform: capitalize;
    }
  }

  &__hint {
    &::first-letter {
      text-transform: capitalize;
    }
  }

  &__input--transparent {
    background: transparent;
  }

  &__actions {
    .btn {
      background: transparent;
      min-width: unset;
    }
  }
}
</style>
